// Institutional-grade MFA verification for UniFed
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Shield } from 'lucide-react';

export const MFAVerifyPage = () => {
  const navigate = useNavigate();
  const { verifyMFA } = useAuth();
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (token.length !== 6) {
      setError('Invalid token length. Require 6 digits.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await verifyMFA(token);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed. Invalid token.');
      setToken('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0B0F14]">
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'linear-gradient(#222A35 1px, transparent 1px), linear-gradient(90deg, #222A35 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }}></div>
      
      <div className="relative z-10 w-full max-w-md px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-[#11161D] border border-[#222A35]">
            <Shield className="w-8 h-8 text-[#4A6B8C]" strokeWidth={1.5} />
          </div>
          
          <h1 className="text-4xl font-bold mb-3 tracking-tight" style={{ fontFamily: 'IBM Plex Sans' }}>
            Multi-Factor Authentication
          </h1>
          
          <p className="text-lg text-[#8A96A3]" style={{ fontFamily: 'Inter' }}>
            Additional verification required
          </p>
        </div>

        <div className="space-y-6">
          <div className="border-t border-[#222A35] pt-6">
            <p className="text-sm text-[#8A96A3] mb-6 uppercase tracking-wide font-medium">
              Enter 6-digit verification code
            </p>

            {error && (
              <div className="bg-[#7D3B3B] border border-[#f87171] px-4 py-3 text-sm mb-6">
                <p className="text-[#f87171] font-mono">{error}</p>
              </div>
            )}

            <div className="flex justify-center mb-6">
              <InputOTP
                maxLength={6}
                value={token}
                onChange={(value) => setToken(value)}
                data-testid="mfa-code-input"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="w-12 h-14 border-[#222A35] bg-[#11161D] text-[#E4E7EB] font-mono text-xl" />
                  <InputOTPSlot index={1} className="w-12 h-14 border-[#222A35] bg-[#11161D] text-[#E4E7EB] font-mono text-xl" />
                  <InputOTPSlot index={2} className="w-12 h-14 border-[#222A35] bg-[#11161D] text-[#E4E7EB] font-mono text-xl" />
                  <InputOTPSlot index={3} className="w-12 h-14 border-[#222A35] bg-[#11161D] text-[#E4E7EB] font-mono text-xl" />
                  <InputOTPSlot index={4} className="w-12 h-14 border-[#222A35] bg-[#11161D] text-[#E4E7EB] font-mono text-xl" />
                  <InputOTPSlot index={5} className="w-12 h-14 border-[#222A35] bg-[#11161D] text-[#E4E7EB] font-mono text-xl" />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <button
              onClick={handleVerify}
              data-testid="mfa-verify-button"
              className="w-full btn-primary"
              disabled={loading || token.length !== 6}
            >
              {loading ? 'VERIFYING...' : 'VERIFY ACCESS'}
            </button>
          </div>

          <div className="border-t border-[#222A35] pt-4">
            <p className="text-xs text-[#5C6A7A] text-center uppercase tracking-widest font-mono">
              Code generated by authenticator application
            </p>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-xs text-[#5C6A7A] uppercase tracking-widest font-mono">
            Zero-trust architecture enforced
          </p>
        </div>
      </div>
    </div>
  );
};
